import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { GraduationCap, Calendar, MapPin, Users, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SchoolsAdmin() {
  const ctx = useOutletContext();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('cards');

  useEffect(() => {
    adminApi.getSchools().then(d => { setSchools(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  return (
    <div data-testid="admin-schools">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Schools Management</h1>
          <p className={`text-sm ${ctx.textSecondary} mt-1`}>{schools.length} universities</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView('cards')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${view === 'cards' ? 'bg-blue-600 text-white' : `${ctx.hoverBg} ${ctx.textSecondary}`}`}>Cards</button>
          <button onClick={() => setView('table')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${view === 'table' ? 'bg-blue-600 text-white' : `${ctx.hoverBg} ${ctx.textSecondary}`}`}>Table</button>
        </div>
      </div>

      {view === 'cards' ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {schools.map(s => (
            <div key={s._id} className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-5`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-sm">{s.name}</h3>
                  <p className={`text-xs ${ctx.textSecondary} flex items-center gap-1 mt-0.5`}><MapPin className="w-3 h-3" />{s.location}</p>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-400">{s.total_reservations} res</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                <div><span className={ctx.textSecondary}>Finals End</span><p className="font-medium mt-0.5">{s.finals_end}</p></div>
                <div><span className={ctx.textSecondary}>Fall Start</span><p className="font-medium mt-0.5">{s.fall_start}</p></div>
                <div><span className={ctx.textSecondary}>Pickup Window</span><p className="font-medium mt-0.5">{s.main_pickup_start} — {s.main_pickup_end}</p></div>
                <div><span className={ctx.textSecondary}>Delivery Window</span><p className="font-medium mt-0.5">{s.delivery_start} — {s.delivery_end}</p></div>
              </div>
              <div className={`border-t ${ctx.borderColor} pt-3 flex items-center justify-between`}>
                <div className="text-xs"><span className={ctx.textSecondary}>Manager:</span> <span className="font-medium">{s.branch_manager}</span></div>
                <button onClick={() => { adminApi.updateSchoolWindows(s._id, {}); toast.success('Windows saved'); }}
                  className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">
                  <Save className="w-3 h-3" /> Save
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${ctx.borderColor}`}>
              {['School', 'Location', 'Finals End', 'Pickup Start', 'Pickup End', 'Delivery Start', 'Delivery End', 'Reservations', 'Manager'].map(h => (
                <th key={h} className={`text-left px-4 py-3 text-xs font-medium ${ctx.textSecondary} uppercase tracking-wider`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{schools.map(s => (
              <tr key={s._id} className={`border-b ${ctx.borderColor} ${ctx.hoverBg}`}>
                <td className="px-4 py-3 font-medium text-sm">{s.name}</td>
                <td className="px-4 py-3 text-xs">{s.location}</td>
                <td className="px-4 py-3 text-xs">{s.finals_end}</td>
                <td className="px-4 py-3 text-xs">{s.main_pickup_start}</td>
                <td className="px-4 py-3 text-xs">{s.main_pickup_end}</td>
                <td className="px-4 py-3 text-xs">{s.delivery_start}</td>
                <td className="px-4 py-3 text-xs">{s.delivery_end}</td>
                <td className="px-4 py-3 text-center">{s.total_reservations}</td>
                <td className="px-4 py-3 text-xs">{s.branch_manager}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
